import humps from 'humps';

// Constants
import { ActionTypes } from 'constants/MogamboConstants';

// Actions
import {
  userHasPermission,
  permissionErrorAlert,
  sendGoogleAnalyticsEvents,
} from '@src/actions/_utils';

// Utils
import { dispatch } from '@src/dispatcher';
import { PERMISSION_LEVELS, GOOGLE_ANALYTICS } from '@src/const';
import { langaugeCodeKeyDecamelize } from '@src/utils';

// API
import * as Api from '../api';

export async function searchForEntities({
  term,
  search_type,
  bot_name,
  callback,
  action = '',
}) {
  if (!userHasPermission(PERMISSION_LEVELS.MOGAMBO_ALL_VIEW)) {
    return permissionErrorAlert();
  }

  sendGoogleAnalyticsEvents(
    GOOGLE_ANALYTICS.CATEGORY.ENTITY,
    GOOGLE_ANALYTICS.EVENTS.ENTITY_SEARCH,
    `entity_search-${term}`,
    GOOGLE_ANALYTICS.VALUE.SINGLE_CLICK
  );

  const response = await Api.searchForEntities({
    term,
    search_type,
    bot_name,
    action,
  });
  if (callback) {
    callback(response.body);
  }
  // TODO: Search for entities using NER after this and merge the results of both
  // the search types
}

export async function fetchEntityData(entityName, entityId) {
  if (!userHasPermission(PERMISSION_LEVELS.MOGAMBO_ALL_VIEW)) {
    return permissionErrorAlert();
  }

  dispatch(ActionTypes.FETCH_ENTITY_DATA_STARTED, {
    entityName,
  });

  Api.getEntityData(entityName, entityId)
    .then((response) => {
      if (response.success) {
        const { body, meta } = response;

        dispatch(ActionTypes.FETCH_ENTITY_DATA_SUCCESS, {
          data: humps.camelizeKeys(body, (key, convert) => {
            return langaugeCodeKeyDecamelize(key, convert);
          }),
          meta,
        });
      } else {
        dispatch(ActionTypes.FETCH_ENTITY_DATA_FAILED, {
          message:
            response.error ||
            'There was an error in fetching the Entity Information',
        });
      }
    })
    .catch((error) => {
      dispatch(ActionTypes.FETCH_ENTITY_DATA_FAILED, {
        message:
          error || 'There was an error in fetching the Entity Information',
      });
    });
}

export function clearEntityData() {
  dispatch(ActionTypes.CLEAR_ENTITY_DATA, {});
}

export async function saveEntityData(data, callback) {
  if (!userHasPermission(PERMISSION_LEVELS.MOGAMBO_ALL_EDIT)) {
    permissionErrorAlert();
    if (callback) {
      callback(false);
    }
    return;
  }

  dispatch(ActionTypes.UPDATE_ENTITY_DATA_STARTED, {});

  sendGoogleAnalyticsEvents(
    GOOGLE_ANALYTICS.CATEGORY.ENTITY,
    GOOGLE_ANALYTICS.EVENTS.ENTITY_CREATE_DONE,
    `entity-${data.name}`,
    GOOGLE_ANALYTICS.VALUE.SINGLE_CLICK
  );

  const { crfData, crfDataEdited, dictionary, ...entityData } = data;

  let mainResponse;
  let error;
  try {
    const reqBody = humps.decamelizeKeys(entityData, (key, convert) => {
      return langaugeCodeKeyDecamelize(key, convert);
    });

    mainResponse = await Api.saveEntityData(data.name, data.id, reqBody);
  } catch (e) {
    error = e;
  }

  if (error) {
    dispatch(ActionTypes.UPDATE_ENTITY_DATA_FAILED, {
      message: error,
    });
    if (callback) callback(false);
    return;
  }

  data.id = mainResponse.body.id;
  dispatch(ActionTypes.UPDATE_ENTITY_DATA_SAVED, {
    data,
  });

  const requests = [];
  if (crfDataEdited && crfData && crfData.sentences) {
    const finalCrfData = { ...crfData };
    finalCrfData.train = true;
    finalCrfData.save = true;
    requests.push(
      Api.saveCRFData(data.name, data.id, humps.decamelizeKeys(finalCrfData))
    );
  }

  if (dictionary) {
    requests.push(
      Api.saveDictionaryData(
        data.name,
        data.id,
        humps.decamelizeKeys(dictionary),
        data.domainName
      )
    );
  }

  let responses = [];
  let success = false;
  let errors = [];
  try {
    responses = await Promise.all(requests);
    success =
      responses.filter((response) => response.success).length ===
      requests.length;
    errors = responses
      .filter((response) => !response.success)
      .map((response) => response.error);
  } catch (e) {
    errors = [e];
  }

  if (success) {
    dispatch(ActionTypes.UPDATE_ENTITY_DATA_SUCCESS, {
      message: 'All changes saved!',
      data,
    });
  } else {
    dispatch(ActionTypes.UPDATE_ENTITY_DATA_FAILED, {
      message:
        errors.length > 0
          ? errors.join(', ')
          : 'There was an error in saving the Entity your changes',
    });
  }

  if (callback) {
    callback(success, data);
  }
}

export async function searchDictionary({
  dictionaryId,
  dictionaryName,
  searchTerm,
  onlyEmptyVariants,
  page,
  pageSize,
}) {
  return Api.searchDictionary({
    dictionaryId,
    dictionaryName,
    searchTerm,
    onlyEmptyVariants,
    page,
    pageSize,
  });
}

export async function addLanguageToDictionary(name, id, data) {
  return Api.addLanguageToDictionary(name, id, data);
}

export async function uploadDictionary(name, id, data) {
  return Api.uploadDictionary(name, id, data);
}
